/*  iceberg-ui  */
import { Button, Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';

/* MUI */
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    CardActions,
    CardContent,
    Grid,
    Typography
} from '@mui/material';

/* Shared */
import { AddClientModal } from 'application/Shared/Components/Client';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { TruncatedText } from 'application/Shared/Components/TruncatedText';
import { FontSizes } from 'application/Shared/Constants';
import { Notify, SessionHelper } from 'application/Shared/Helpers';
import { Confirm, ModalFormFooter } from 'application/Shared/Modals';
import { ProviderType } from 'application/Shared/types/tenantProvider';

/* npm */
import axios from 'axios';

/* Flags */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { OktaModal } from './OktaModal';
import { SaasOpsIsConnectedChip } from './SaasOps/SaasOpsIsConnectedChip';

const identityManagementConfigOptions = [
    {
        description: 'Onboarding.quickScanSelectStep.okta.intro',
        displayName: 'Common.okta',
        displayNameAlt: 'Common.okta',
        logo: 'okta_logo.svg',
        type: ProviderType.Okta
    },
    {
        description: 'Onboarding.quickScanSelectStep.google.intro',
        displayName: 'Common.google',
        displayNameAlt: 'Common.google_workspace',
        logo: 'google_g_logo.svg',
        type: ProviderType.Google
    },
    {
        description: 'Onboarding.quickScanSelectStep.microsoftEntraID.intro',
        displayName: 'Common.microsoft',
        displayNameAlt: 'Common.microsoftEntraID',
        logo: 'microsoft_logo.svg',
        type: ProviderType.Microsoft
    }
];

/**
 * Based on the provider's type, determine if the identity integration is configurable.
 * Once a tenant integrates with one type of integration, they can no longer integrate with a different one.
 */
const determineIsConfigurable = (connection, provider) => {
    const providerType = provider?.providerType;

    // based on the connection being google or azure/Microsoft Entra ID AND whatever the provider is set to, mark this isConfigurable.
    if (!providerType) return true;

    return (
        connection.type === providerType ||
        connection.type === ProviderType.Okta
    );
};

/**
 * Renders a card for an individual integration connection
 * @param {object} props
 * @param {object} props.connection the Connection to be rendered
 */
const ConnectionCard = ({
    connection,
    clientTenant,
    provider = {},
    refetchClientData
}) => {
    const { t } = useTranslation();
    const session = useSession();
    const userTheme = getUserTheme(session?.user?.theme);

    const { connectionDetails = {} } = provider;

    const isEnabled =
        provider.providerType === connection.type && provider.enabled;
    const { isUpdateRequired } = connectionDetails;

    const isConfigurable = determineIsConfigurable(connection, provider);

    const [showModal, setShowModal] = useState(false);
    const [showOktaModal, setShowOktaModal] = useState(false);
    const [promptDelete, setPromptDelete] = useState(false);

    const sessionHelper = new SessionHelper(session);
    const hasPermissionToDisconnect = sessionHelper.hasPermission(
        ['partner', 'system_admin'],
        'and'
    );

    const reload = () => {
        setShowModal(false);
        setShowOktaModal(false);
        if (refetchClientData) {
            refetchClientData();
        }
    };

    const tenantToEditIntegration = {
        ...clientTenant,
        providerType: connection.type
    };

    const skipAnimation = !!connectionDetails.id;

    const imgSrc = `/images/${userTheme}/integrations/${connection.logo}`;

    const disconnectIntegration = async () => {
        try {
            await axios.put(`/tenantProvider/${provider.id}/reset`, {
                platformTenantId: clientTenant.id
            });
            refetchClientData();
            Notify.s(t('Provider.Shared.saved'));
        } catch (e) {
            Notify.e(t('Provider.Shared.error_saving_settings'));
        }
    };

    const btnAction = () => {
        if (isEnabled) {
            if (isUpdateRequired) {
                return 'update';
            }
            return 'disconnect';
        }
        return 'configure';
    };

    const onClick = () => {
        if (btnAction() === 'disconnect') {
            setPromptDelete(true);
        } else if (connection.type === ProviderType.Okta) {
            setShowOktaModal(true);
        } else {
            setShowModal(true);
        }
    };

    const btnDisableTooltipText = () => {
        if (!isConfigurable) {
            return t('Integrations.identity.management.disabledButton');
        }
        if (btnAction() === 'disconnect' && !hasPermissionToDisconnect) {
            return t(
                'Integrations.identity.management.disabledButton.noPermissions'
            );
        }
        return null;
    };

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    backgroundColor:
                        'onboarding.integrationCardBackgroundColor',
                    minHeight: '300px'
                }}
            >
                <CardContent>
                    <Flex sx={{ alignItems: 'center', marginY: '16px' }}>
                        <img
                            alt={t(connection.displayName)}
                            id="integrationImage"
                            src={imgSrc}
                            style={{
                                height: '30px',
                                marginRight: '16px'
                            }}
                        />
                        <Typography variant="h5">
                            {t(connection.displayName)}
                        </Typography>
                    </Flex>
                    <Box height="100px">
                        <TruncatedText
                            text={t(connection.description)}
                            maxLines={6}
                            showTooltip
                        />
                    </Box>
                </CardContent>
                <CardActions
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '16px',
                        minHeight: '45px',
                        padding: '16px'
                    }}
                >
                    <>
                        <DarkTooltip arrow title={btnDisableTooltipText()}>
                            <Box>
                                <Button
                                    data-trackid={`identityManagementIntegrations_${btnAction()}_${
                                        connection.type
                                    }`}
                                    disabled={
                                        !isConfigurable ||
                                        (btnAction() === 'disconnect' &&
                                            !hasPermissionToDisconnect)
                                    }
                                    onClick={() => onClick()}
                                    sx={{ minWidth: 'unset' }}
                                >
                                    {t(`Common.${btnAction()}`)}
                                </Button>
                            </Box>
                        </DarkTooltip>
                        {isEnabled && (
                            <SaasOpsIsConnectedChip
                                isConnected={!isUpdateRequired}
                                lostConnection={isUpdateRequired}
                            />
                        )}
                    </>
                </CardActions>
            </Card>
            {showModal && (
                <AddClientModal
                    formFooterComponent={
                        <ModalFormFooter onClose={() => setShowModal(false)} />
                    }
                    filterExistingSyncedTenants={false}
                    show={showModal}
                    showDescription
                    skipToChoosingIntegration
                    tenantToEditIntegration={tenantToEditIntegration}
                    onClose={() => setShowModal(false)}
                    useAnIntegrationMethod="true"
                    onSuccess={reload}
                    skipAnimation={skipAnimation}
                />
            )}

            <OktaModal
                clientId={clientTenant.id}
                isOpen={showOktaModal}
                onClose={() => setShowOktaModal(false)}
                onSubmit={reload}
            />

            {promptDelete && (
                <Confirm
                    modalOpen={!!promptDelete}
                    promptConfirmFn={() => disconnectIntegration()}
                    promptCancelFn={() => setPromptDelete(false)}
                    promptTitle={t('Common.disconnectConfirmation')}
                    promptText={
                        <Trans
                            i18nKey="Clients.disconnectIntegration"
                            values={{
                                integration: t(connection.displayNameAlt)
                            }}
                            components={{
                                integration: (
                                    <Typography
                                        variant="span"
                                        color="textSecondary"
                                        fontWeight={600}
                                    />
                                )
                            }}
                        />
                    }
                    textConfirmButton={t('Common.disconnect')}
                />
            )}
        </>
    );
};

/**
 * Renders the set of integration connection cards for the specified tenant
 * @param {object} props
 * @param {string} props.clientId the ID of the client tenant to have integrations rendered for
 * @param {string} props.filter filters connections by their display name
 */
const IdentityManagementConnections = ({
    filter,
    clientTenant,
    providers,
    refetchClientData
}) => {
    const { t } = useTranslation();

    const connections = identityManagementConfigOptions.filter(
        (conn) => t(conn.displayName).search(new RegExp(filter, 'i')) !== -1
    );

    return (
        <Grid item container spacing={3} xs={12}>
            <Grid item xs={12}>
                <Typography variant="h6" color="textSecondary" fontWeight={600}>
                    {t('Integrations.availableAndSupported')}
                </Typography>
            </Grid>
            {connections.map((conn) => {
                const provider =
                    providers?.find((p) => p.providerType === conn.type) ??
                    providers?.find(
                        (p) =>
                            p.providerType === ProviderType.Microsoft ||
                            p.providerType === ProviderType.Google
                    );
                return (
                    <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        xl={3}
                        key={t(conn.displayName)}
                    >
                        <ConnectionCard
                            connection={conn}
                            clientTenant={clientTenant}
                            provider={provider}
                            refetchClientData={refetchClientData}
                        />
                    </Grid>
                );
            })}
        </Grid>
    );
};

export const IdentityManagementIntegrations = ({
    clientTenant,
    providers,
    filter,
    isCollapsible,
    refetchClientData,
    defaultExpanded = true
}) => {
    const { t } = useTranslation();

    return (
        <Accordion
            slotProps={{ transition: { unmountOnExit: true } }}
            defaultExpanded={defaultExpanded}
        >
            {isCollapsible && (
                <AccordionSummary
                    expandIcon={
                        <IconWithVariant
                            data-testid="expandAccordionIconIdentityManagementIntegrations"
                            variant="primary"
                            src="ChevronDown"
                            size={FontSizes[2]}
                        />
                    }
                >
                    <Box display="flex" alignItems="center">
                        <IconWithVariant
                            variant="primary"
                            src="PuzzlePiece"
                            size={FontSizes[3]}
                            wrapperProps={{ marginRight: '8px' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('Integrations.identity.management')}
                        </Typography>
                    </Box>
                </AccordionSummary>
            )}
            <AccordionDetails>
                <Box mb="16px">
                    <Alert
                        severity="warning"
                        icon={false}
                        sx={{ justifyContent: 'center' }}
                    >
                        {t('Clients.integration.billingDisclaimer')}
                    </Alert>
                    <Typography
                        variant="body2"
                        mt="24px"
                        dangerouslySetInnerHTML={{
                            __html: t('Common.identityManagementIntegrations')
                        }}
                    />
                </Box>
                <IdentityManagementConnections
                    clientTenant={clientTenant}
                    providers={providers}
                    filter={filter}
                    refetchClientData={refetchClientData}
                />
            </AccordionDetails>
        </Accordion>
    );
};
